import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function pad(n) { return String(n).padStart(2,'0'); }

function buildCalendar(year, month) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
}

/**
 * DateTimePicker
 * Props:
 *   value       – ISO string or ''
 *   onChange    – (isoString) => void
 *   min         – ISO string (optional)
 *   label       – string
 *   placeholder – string
 */
export default function DateTimePicker({ value, onChange, min, label, placeholder = 'Pick date & time' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Parse value to a Date or null
    const parsed = value ? new Date(value) : null;
    const [viewYear,  setViewYear]  = useState(() => parsed ? parsed.getFullYear()  : new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(() => parsed ? parsed.getMonth()     : new Date().getMonth());
    const [selDate,   setSelDate]   = useState(() => parsed || null);
    const [hour,      setHour]      = useState(() => parsed ? parsed.getHours()     : 9);
    const [minute,    setMinute]    = useState(() => parsed ? Math.floor(parsed.getMinutes() / 5) * 5 : 0);

    // Keep internal state in sync when value changes from outside
    useEffect(() => {
        const d = value ? new Date(value) : null;
        setSelDate(d);
        if (d) {
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
            setHour(d.getHours());
            setMinute(Math.floor(d.getMinutes() / 5) * 5);
        }
    }, [value]);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const minDate = min ? new Date(min) : null;

    const isDisabled = (day) => {
        if (!day || !minDate) return false;
        const cell = new Date(viewYear, viewMonth, day, 23, 59, 59);
        return cell < minDate;
    };

    const isToday = (day) => {
        const now = new Date();
        return day && viewYear === now.getFullYear() && viewMonth === now.getMonth() && day === now.getDate();
    };

    const isSelected = (day) => {
        return day && selDate &&
            viewYear  === selDate.getFullYear()  &&
            viewMonth === selDate.getMonth()      &&
            day       === selDate.getDate();
    };

    const selectDay = (day) => {
        if (!day || isDisabled(day)) return;
        const d = new Date(viewYear, viewMonth, day, hour, minute, 0);
        setSelDate(d);
        onChange(d.toISOString().slice(0, 16)); // keep yyyy-MM-ddTHH:mm format
    };

    const commitTime = (h, m) => {
        if (!selDate) return;
        const d = new Date(selDate.getFullYear(), selDate.getMonth(), selDate.getDate(), h, m, 0);
        setSelDate(d);
        onChange(d.toISOString().slice(0, 16));
    };

    const prevMonth = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    };

    const displayValue = selDate
        ? `${pad(selDate.getDate())} ${MONTHS[selDate.getMonth()].slice(0,3)} ${selDate.getFullYear()}  ${pad(hour)}:${pad(minute)}`
        : '';

    const cells = buildCalendar(viewYear, viewMonth);
    const hours   = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

    return (
        <div ref={ref} style={{ position: 'relative', width: '100%' }}>
            {/* Trigger input */}
            <div
                className="cef-input dtp-trigger"
                onClick={() => setOpen(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
            >
                <Calendar size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                <span style={{ color: displayValue ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: '0.9rem', flex: 1 }}>
                    {displayValue || placeholder}
                </span>
                {selDate && (
                    <button
                        type="button"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: 0 }}
                        onClick={e => { e.stopPropagation(); setSelDate(null); onChange(''); }}
                    >×</button>
                )}
            </div>

            {open && (
                <div className="dtp-panel">
                    {/* Month navigation */}
                    <div className="dtp-header">
                        <button type="button" className="dtp-nav-btn" onClick={prevMonth}><ChevronLeft size={16} /></button>
                        <span className="dtp-month-label">{MONTHS[viewMonth]} {viewYear}</span>
                        <button type="button" className="dtp-nav-btn" onClick={nextMonth}><ChevronRight size={16} /></button>
                    </div>

                    {/* Calendar grid */}
                    <div className="dtp-grid">
                        {DAYS.map(d => <div key={d} className="dtp-cell dtp-weekday">{d}</div>)}
                        {cells.map((day, i) => (
                            <div
                                key={i}
                                className={[
                                    'dtp-cell',
                                    day ? 'dtp-day' : '',
                                    isSelected(day) ? 'dtp-selected' : '',
                                    isToday(day) && !isSelected(day) ? 'dtp-today' : '',
                                    isDisabled(day) ? 'dtp-disabled' : ''
                                ].filter(Boolean).join(' ')}
                                onClick={() => selectDay(day)}
                            >
                                {day || ''}
                            </div>
                        ))}
                    </div>

                    {/* Time row */}
                    <div className="dtp-time-row">
                        <Clock size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        <select
                            className="dtp-select"
                            value={hour}
                            onChange={e => { const h = +e.target.value; setHour(h); commitTime(h, minute); }}
                        >
                            {hours.map(h => <option key={h} value={h}>{pad(h)}</option>)}
                        </select>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>:</span>
                        <select
                            className="dtp-select"
                            value={minute}
                            onChange={e => { const m = +e.target.value; setMinute(m); commitTime(hour, m); }}
                        >
                            {minutes.map(m => <option key={m} value={m}>{pad(m)}</option>)}
                        </select>
                    </div>

                    {/* Footer */}
                    <div className="dtp-footer">
                        <button type="button" className="dtp-clear-btn" onClick={() => { setSelDate(null); onChange(''); }}>Clear</button>
                        <button type="button" className="dtp-done-btn" onClick={() => setOpen(false)}>Done</button>
                    </div>
                </div>
            )}
        </div>
    );
}
